import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';

import 'moment/locale/ja';

moment.locale('ja');

const SHIFTS_API = 'https://system.sales-management.cloud/api/shifts.php';

function MyShift() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [savingDate, setSavingDate] = useState('');
  const [saveStatusByDate, setSaveStatusByDate] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [formByDate, setFormByDate] = useState({});
  const autoSaveTimersRef = useRef({});

  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfo = Cookies.get('user_info');
    const user = userInfo ? JSON.parse(userInfo) : null;
    return { token, user };
  }, []);

  const monthKey = useMemo(() => moment(currentDate).format('YYYY-MM'), [currentDate]);

  const fetchMonthData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { token, user } = getAuthData();
      if (!token || !user?.id) {
        setError('認証情報が見つかりません');
        return;
      }

      setCurrentUserId(user.id);

      const year = moment(currentDate).format('YYYY');
      const month = moment(currentDate).format('M');
      const response = await fetch(`${SHIFTS_API}?year=${year}&month=${month}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.status !== 'success') {
        setError(data.message || 'シフトデータ取得に失敗しました');
        return;
      }

      const nextForm = {};
      const start = moment(currentDate).startOf('month');
      const end = moment(currentDate).endOf('month');
      let day = start.clone();

      while (day.isSameOrBefore(end, 'day')) {
        const dateKey = day.format('YYYY-MM-DD');
        const dayData = data.data?.[dateKey]?.shifts || [];
        const myShift = dayData.find((row) => Number(row.user_id) === Number(user.id));
        nextForm[dateKey] = {
          enabled: !!myShift,
          shift_in: myShift?.shift_in ? String(myShift.shift_in).substring(0, 5) : '09:00',
          shift_out: myShift?.shift_out ? String(myShift.shift_out).substring(0, 5) : '18:00',
        };
        day = day.add(1, 'day');
      }

      setFormByDate(nextForm);
      setSaveStatusByDate({});
    } catch (e) {
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [currentDate, getAuthData]);

  useEffect(() => {
    fetchMonthData();
  }, [fetchMonthData]);

  useEffect(() => {
    return () => {
      Object.values(autoSaveTimersRef.current).forEach((timer) => clearTimeout(timer));
      autoSaveTimersRef.current = {};
    };
  }, []);

  const saveDay = useCallback(async (dateKey, row) => {
    setError('');
    setSuccess('');

    if (!row) return;

    const today = moment().format('YYYY-MM-DD');
    if (moment(dateKey).isSameOrBefore(today, 'day')) {
      setError('今日より後の日付のみ操作できます');
      return;
    }

    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      setSavingDate(dateKey);
      setSaveStatusByDate((prev) => ({ ...prev, [dateKey]: 'saving' }));

      if (!row.enabled) {
        const res = await fetch(SHIFTS_API, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ date: dateKey }),
        });
        const data = await res.json();
        if (data.status !== 'success' && !(res.status === 404 || data.message === 'Shift not found for this date')) {
          setError(data.message || 'シフト解除に失敗しました');
          setSaveStatusByDate((prev) => ({ ...prev, [dateKey]: 'error' }));
          return;
        }
        setSaveStatusByDate((prev) => ({ ...prev, [dateKey]: 'saved' }));
        setSuccess(`${dateKey} を自動保存しました`);
        return;
      }

      const res = await fetch(SHIFTS_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          date: dateKey,
          shift_in: row.shift_in,
          shift_out: row.shift_out,
        }),
      });
      const data = await res.json();
      if (data.status !== 'success') {
        setError(data.message || 'シフト保存に失敗しました');
        setSaveStatusByDate((prev) => ({ ...prev, [dateKey]: 'error' }));
        return;
      }
      setSaveStatusByDate((prev) => ({ ...prev, [dateKey]: 'saved' }));
      setSuccess(`${dateKey} を自動保存しました`);
    } catch (e) {
      setError('通信エラーが発生しました');
      setSaveStatusByDate((prev) => ({ ...prev, [dateKey]: 'error' }));
    } finally {
      setSavingDate('');
    }
  }, [getAuthData]);

  const scheduleAutoSave = useCallback((dateKey, row) => {
    if (autoSaveTimersRef.current[dateKey]) {
      clearTimeout(autoSaveTimersRef.current[dateKey]);
    }
    autoSaveTimersRef.current[dateKey] = setTimeout(() => {
      saveDay(dateKey, row);
    }, 500);
  }, [saveDay]);

  const onChangeField = useCallback((dateKey, key, value) => {
    const today = moment().format('YYYY-MM-DD');
    if (moment(dateKey).isSameOrBefore(today, 'day')) {
      return;
    }

    setFormByDate((prev) => {
      const nextRow = {
        ...(prev[dateKey] || { enabled: false, shift_in: '09:00', shift_out: '18:00' }),
        [key]: value,
      };
      scheduleAutoSave(dateKey, nextRow);
      setSaveStatusByDate((statusPrev) => ({ ...statusPrev, [dateKey]: 'pending' }));

      return {
        ...prev,
        [dateKey]: nextRow,
      };
    });
  }, [scheduleAutoSave]);

  const goPrevMonth = () => setCurrentDate((prev) => moment(prev).subtract(1, 'month').toDate());
  const goNextMonth = () => setCurrentDate((prev) => moment(prev).add(1, 'month').toDate());
  const goThisMonth = () => setCurrentDate(new Date());

  const days = useMemo(() => {
    const start = moment(currentDate).startOf('month');
    const end = moment(currentDate).endOf('month');
    const out = [];
    let day = start.clone();
    while (day.isSameOrBefore(end, 'day')) {
      out.push(day.clone());
      day = day.add(1, 'day');
    }
    return out;
  }, [currentDate]);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <div className="mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">自分の月次シフト管理</h1>
        <div className="flex items-center gap-2">
          <button onClick={goPrevMonth} className="px-3 py-2 bg-gray-200 rounded-md text-sm hover:bg-gray-300">前月</button>
          <button onClick={goThisMonth} className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">今月</button>
          <button onClick={goNextMonth} className="px-3 py-2 bg-gray-200 rounded-md text-sm hover:bg-gray-300">次月</button>
        </div>
      </div>

      <p className="mb-4 text-sm text-gray-600">{monthKey} / ログインユーザーID: {currentUserId ?? '-'}</p>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">{success}</div>}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-12 bg-gray-50 text-xs md:text-sm font-semibold text-gray-700 border-b">
          <div className="col-span-3 p-3">日付</div>
          <div className="col-span-2 p-3">出勤予定</div>
          <div className="col-span-2 p-3">出勤時刻</div>
          <div className="col-span-2 p-3">退勤時刻</div>
          <div className="col-span-3 p-3">状態</div>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-500">読み込み中...</div>
        ) : (
          days.map((day) => {
            const dateKey = day.format('YYYY-MM-DD');
            const row = formByDate[dateKey] || { enabled: false, shift_in: '09:00', shift_out: '18:00' };
            const isPastOrToday = day.isSameOrBefore(moment(), 'day');
            const saving = savingDate === dateKey;
            const saveStatus = saveStatusByDate[dateKey] || '';

            return (
              <div key={dateKey} className="grid grid-cols-12 border-b last:border-b-0 text-sm items-center">
                <div className="col-span-3 p-3">
                  <div className="font-medium text-gray-800">{dateKey}</div>
                  <div className="text-xs text-gray-500">{day.format('(ddd)')}</div>
                </div>

                <div className="col-span-2 p-3">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      disabled={isPastOrToday || saving}
                      onChange={(e) => onChangeField(dateKey, 'enabled', e.target.checked)}
                    />
                    <span>{row.enabled ? 'あり' : 'なし'}</span>
                  </label>
                </div>

                <div className="col-span-2 p-3">
                  <input
                    type="time"
                    value={row.shift_in}
                    disabled={isPastOrToday || !row.enabled || saving}
                    onChange={(e) => onChangeField(dateKey, 'shift_in', e.target.value)}
                    className="w-full border rounded px-2 py-1"
                  />
                </div>

                <div className="col-span-2 p-3">
                  <input
                    type="time"
                    value={row.shift_out}
                    disabled={isPastOrToday || !row.enabled || saving}
                    onChange={(e) => onChangeField(dateKey, 'shift_out', e.target.value)}
                    className="w-full border rounded px-2 py-1"
                  />
                </div>

                <div className="col-span-3 p-3">
                  {isPastOrToday ? (
                    <span className="text-xs text-gray-500">今日以前は変更不可</span>
                  ) : (
                    <span className="text-xs text-gray-600">
                      {saving || saveStatus === 'saving'
                        ? '保存中...'
                        : saveStatus === 'pending'
                        ? '変更あり（自動保存待ち）'
                        : saveStatus === 'saved'
                        ? '自動保存済み'
                        : saveStatus === 'error'
                        ? '保存失敗'
                        : '未変更'}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default MyShift;
